import { useQueryClient } from "@tanstack/react-query";
import { Languages } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import { SettingsModalLayout } from "@/features/user-setting/components/SettingsModalLayout";
import {
  getShowTranslatedContentFromStorage,
  invalidateDisplayContentQueries,
  setShowTranslatedContentInStorage,
} from "@/lib/displayPreferences";

interface DisplayPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DisplayPreferencesModal: React.FC<
  DisplayPreferencesModalProps
> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showTranslatedContent, setShowTranslatedContent] = useState(true);

  useEffect(() => {
    if (!isOpen || typeof window === "undefined") {
      return;
    }

    setShowTranslatedContent(getShowTranslatedContentFromStorage());
  }, [isOpen]);

  if (!isOpen) return null;

  const handleShowTranslatedChange = (checked: boolean) => {
    setShowTranslatedContent(checked);
    setShowTranslatedContentInStorage(checked);
    invalidateDisplayContentQueries(queryClient);
  };

  return (
    <SettingsModalLayout
      isOpen={isOpen}
      onClose={onClose}
      icon={Languages}
      title={t("common.displaySettings")}
    >
      <div className="rounded-lg border border-gray-200 p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <h3 className="text-sm font-semibold text-gray-900">
              {t("common.showTranslatedContent")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t("common.showTranslatedContentDescription")}
            </p>
          </div>
          <Switch
            checked={showTranslatedContent}
            onCheckedChange={handleShowTranslatedChange}
            aria-label={t("common.showTranslatedContent")}
          />
        </div>
      </div>
    </SettingsModalLayout>
  );
};
